import { useEffect, useMemo, useState } from 'react';
import { toCelldataSheet } from './converters';
import { parseSheetBytes } from './parseSheets';
import { FortuneSheet } from './types';

export type LoadingStage = 'downloading' | 'parsing' | null;

export interface UseResolveSheetsArgs {
    /** In-memory sheets (import / composed). When present, skips the fetch. */
    inMemorySheets?: FortuneSheet[];
    /** Presigned URL of the xlsx/csv to fetch + parse. */
    fileUrl?: string;
    /** Resolved MIME type — drives xlsx vs csv parsing on the fetch path. */
    mimeType?: string;
    /** Original file name — a parse hint for extension-based fallback. */
    fileName?: string;
    /** Gate the fetch (the drawer only resolves while open). Defaults to true. */
    open?: boolean;
    /**
     * When the fetch path parses to zero sheets, use this instead. The editable
     * drawer passes a single blank sheet so the grid is usable; the read-only
     * view passes nothing so it can render its "no data" state. MUST be a stable
     * reference (module constant) — it is an effect dependency.
     */
    emptyFallback?: FortuneSheet[];
}

/**
 * Resolve the sheets a Fortune-Sheet `<Workbook>` should mount: either in-memory
 * sheets passed by the caller, or the result of fetching + worker-parsing a
 * presigned URL, with loading/stage/error tracking. Shared by SheetEditorDrawer
 * (via useSheetEditorDrawerLogic) and the read-only SheetView so the
 * fetch→arrayBuffer→parseSheetBytes flow lives in exactly one place.
 *
 * Returned `sheets` are normalised to celldata form (toCelldataSheet): a saved
 * sheet round-trips as the runtime 2D `data` from getAllSheets(), which
 * `<Workbook data>` can't initialise from — without this a reopened saved sheet
 * renders an empty grid. No-op for import-path sheets that already carry celldata.
 */
export const useResolveSheets = ({ inMemorySheets, fileUrl, mimeType, fileName, open = true, emptyFallback }: UseResolveSheetsArgs) => {
    const [fetchedSheets, setFetchedSheets] = useState<FortuneSheet[] | null>(null);
    const [loading, setLoading] = useState(!inMemorySheets);
    const [loadingStage, setLoadingStage] = useState<LoadingStage>(!inMemorySheets && fileUrl ? 'downloading' : null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // In-memory path: nothing to fetch.
        if (inMemorySheets) {
            setLoading(false);
            setLoadingStage(null);
            setError(null);
            return undefined;
        }

        // Nothing to fetch and nothing in memory: settle instead of spinning forever.
        if (open && !fileUrl) {
            setLoading(false);
            setLoadingStage(null);
            setFetchedSheets(emptyFallback ?? []);
            return undefined;
        }

        if (!open || !fileUrl) return undefined;
        const controller = new AbortController();
        let cancelled = false;
        setLoading(true);
        setLoadingStage('downloading');
        setError(null);
        setFetchedSheets(null);

        (async () => {
            try {
                const response = await fetch(fileUrl, { signal: controller.signal });
                if (!response.ok) throw new Error('Failed to fetch file');
                const buffer = await response.arrayBuffer();
                setLoadingStage('parsing');
                const parsed = await parseSheetBytes(buffer, mimeType, fileName);
                if (cancelled) return;
                setFetchedSheets(parsed.length ? parsed : emptyFallback ?? parsed);
                setLoading(false);
                setLoadingStage(null);
            } catch (err) {
                if ((err as DOMException)?.name === 'AbortError') return;
                if (cancelled) return;
                setError(err instanceof Error ? err.message : 'Failed to load file');
                setLoading(false);
                setLoadingStage(null);
            }
        })();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [open, fileUrl, mimeType, fileName, inMemorySheets, emptyFallback]);

    const rawSheets = inMemorySheets ?? fetchedSheets;
    const sheets = useMemo(() => (rawSheets ? rawSheets.map(toCelldataSheet) : rawSheets), [rawSheets]);

    return { sheets, loading, loadingStage, error };
};
